/**
 * LeadsTab — Phase 3 redesign.
 *
 * Replaces the inline leads markup in LeadBoard.js with the v2 design:
 *  • PageHead with title + Add Contact button
 *  • 4 StatCards (Total / Qualified / Showing / Verified)
 *  • Inline AddContactInline panel (replaces AddContactModal — NO modal)
 *  • ListToolbar (search + status + grade + date-range)
 *  • DataTable with avatar/name/business/source chip/grade/score/status/CTA
 *  • Bulk-action bar (Share / Delete with reason)
 *  • Pager (25/50/100 page size)
 *  • Inline ExportPanel popover (replaces ExportModal — NO modal)
 *  • Inline SharePanel popover (replaces ShareLeadModal — NO modal)
 *
 * Every backend endpoint and existing data-testid that the user-facing
 * documentation relies on is preserved.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Users, CheckCircle2, Eye as EyeIcon, Mail, Plus, Download, Trash2, Share2, Filter, X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  StatCard, PageHead, Btn, Badge, StatusBadge, SourceChip, Score, Check,
  Avatar, ListToolbar, Pager, RowActions, Popover, EmptyState, TableSkeleton, Modal,
  SortHeader, TabError,
} from '../ui/primitives';
import AddContactInline from '../panels/AddContactInline';
import ExportPanel from '../panels/ExportPanel';
import SharePanel from '../panels/SharePanel';

const STATUS_OPTIONS = [
  { value: 'qualified', label: 'Qualified' },
  { value: 'verified', label: 'Verified' },
  { value: 'initiated', label: 'Initiated' },
  { value: 'invalid_email', label: 'Invalid email' },
];
const GRADE_OPTIONS = [
  { value: 'A', label: 'Grade A' },
  { value: 'B', label: 'Grade B' },
  { value: 'C', label: 'Grade C' },
  { value: 'D', label: 'Grade D' },
];

export default function LeadsTab({
  // data
  leads, filteredLeads, paginatedLeads, isLoadingLeads, leadsError, onRetryLeads,
  totalLeads, qualifiedLeads, showingLeads, verifiedLeads,
  // filters
  searchTerm, setSearchTerm,
  statusFilter, setStatusFilter,
  gradeFilter, setGradeFilter,
  dateRange, setDateRange,
  clearFilters, hasActiveFilters,
  // pagination
  currentPage, setCurrentPage, pageSize, setPageSize,
  // selection
  selectedLeads, setSelectedLeads,
  // role
  currentUser, canExportFn, canDeleteFn,
  // handlers (provided by LeadBoard parent)
  onView, onShare, onExport, onAddContact, onBulkDelete,
  // team list for share popover
  teamMembers = [],
  // operational flags
  isExporting, isDeletingBulk, isSharing, isAdding,
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const exportBtnRef = useRef(null);
  const [shareForLead, setShareForLead] = useState(null);
  const shareAnchors = useRef({});
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkDeleteReason, setBulkDeleteReason] = useState('');
  // Phase 4 polish — client-side sort state for the current page
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const onSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortedPage = (() => {
    const data = [...paginatedLeads];
    data.sort((a, b) => {
      let av = a[sortField]; let bv = b[sortField];
      if (sortField === 'name') { av = `${a.first_name || ''} ${a.last_name || ''}`.trim(); bv = `${b.first_name || ''} ${b.last_name || ''}`.trim(); }
      if (av == null) av = '';
      if (bv == null) bv = '';
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return data;
  })();

  const total = filteredLeads?.length || 0;

  // Selection helpers
  const isAllSelected = paginatedLeads.length > 0 && (paginatedLeads || []).every((l) => selectedLeads.includes(l._id || l.id));
  const toggleAll = () => {
    if (isAllSelected) {
      setSelectedLeads((selectedLeads || []).filter((id) => !(paginatedLeads || []).some((l) => (l._id || l.id) === id)));
    } else {
      const visibleIds = (paginatedLeads || []).map((l) => l._id || l.id);
      setSelectedLeads(Array.from(new Set([...selectedLeads, ...visibleIds])));
    }
  };
  const toggleOne = (id) => {
    setSelectedLeads(selectedLeads.includes(id) ? (selectedLeads || []).filter((x) => x !== id) : [...selectedLeads, id]);
  };

  const canExport = canExportFn ? canExportFn(currentUser, 'leads') : currentUser?.role === 'admin';
  const canDelete = canDeleteFn ? canDeleteFn(currentUser, 'leads') : currentUser?.role === 'admin';

  return (
    <div data-testid="tab-leads">
      <PageHead
        title="Leads"
        sub={isLoadingLeads ? 'Loading…' : `${total.toLocaleString()} total · ${verifiedLeads || 0} verified`}
        actions={(
          <>
            {canExport && total > 0 && (
              <div ref={exportBtnRef} style={{ position: 'relative' }}>
                <Btn data-testid="leads-tab-btn-10" variant="ghost" icon={Download} onClick={() => setShowExport((s) => !s)} testid="export-leads-button">
                  {isExporting ? 'Exporting…' : 'Export'}
                </Btn>
                <Popover open={showExport} onClose={() => setShowExport(false)} anchorRef={exportBtnRef}>
                  <ExportPanel
                    onClose={() => setShowExport(false)}
                    onExport={({ format, fields, exportType }) => {
                      onExport({ format, fields, exportType });
                      setShowExport(false);
                    }}
                    isLoading={isExporting}
                    totalCount={total}
                  />
                </Popover>
              </div>
            )}
            <Btn data-testid="leads-tab-btn-9" variant="primary" icon={Plus} onClick={() => setShowAdd(true)} testid="add-contact-button">
              Add contact
            </Btn>
          </>
        )}
        testid="page-head"
      />

      {/* Stat grid */}
      <div className="lb-stat-grid" style={{ marginBottom: 16 }} data-testid="leads-stats">
        <StatCard icon={Users} value={totalLeads || 0} label="Total leads" testid="stat-total" />
        <StatCard icon={CheckCircle2} value={qualifiedLeads || 0} label="Qualified (A/B)" testid="stat-qualified" />
        <StatCard icon={EyeIcon} value={showingLeads || 0} label="Showing" testid="stat-showing" />
        <StatCard icon={Mail} value={verifiedLeads || 0} label="Email verified" testid="stat-verified" />
      </div>

      {/* Inline AddContact panel */}
      {showAdd && (
        <AddContactInline
          defaultSource="manual"
          onClose={() => setShowAdd(false)}
          onCreate={async (form) => {
            await onAddContact(form);
            setShowAdd(false);
          }}
          isLoading={isAdding}
        />
      )}

      {/* Error retry UI — Phase 4 polish */}
      {leadsError && !isLoadingLeads && (
        <div className="lb-card" style={{ marginBottom: 14 }}>
          <TabError message={leadsError} onRetry={onRetryLeads} testid="leads-error" />
        </div>
      )}

      {/* Table card: toolbar + bulk bar + table + pager */}
      <div className="lb-card" data-testid="leads-table-card">
        <ListToolbar
          search={searchTerm}
          setSearch={setSearchTerm}
          status={statusFilter && statusFilter !== 'all' ? statusFilter : ''}
          setStatus={(v) => setStatusFilter(v || 'all')}
          statusOptions={STATUS_OPTIONS}
          source={gradeFilter && gradeFilter !== 'all' ? gradeFilter : ''}
          setSource={(v) => setGradeFilter(v || 'all')}
          sourceOptions={GRADE_OPTIONS}
          range={dateRange}
          setRange={setDateRange}
          extraRight={hasActiveFilters ? <Btn data-testid="leads-tab-btn-8" variant="ghost" sm icon={X} onClick={clearFilters} testid="clear-filters-button">Clear filters</Btn> : null}
        />

        {selectedLeads.length > 0 && (
          <div className="lb-bulkbar" data-testid="bulk-bar">
            <b>{selectedLeads.length} selected</b>
            <span className="lb-spacer" />
            {canDelete && (
              <Btn data-testid="leads-tab-btn-7" variant="danger" sm icon={Trash2} onClick={() => setConfirmBulkDelete(true)} testid="bulk-delete-button">
                Delete
              </Btn>
            )}
            <Btn data-testid="leads-tab-btn-6" variant="ghost" sm onClick={() => setSelectedLeads([])} testid="bulk-clear">Clear</Btn>
          </div>
        )}

        <div className="lb-tablewrap">
          <div className="lb-tablescroll">
            <table className="lb-dt">
              <thead>
                <tr>
                  <th style={{ width: 38 }}><Check data-testid="leads-tab-check-2" on={isAllSelected} onClick={toggleAll} testid="select-all" /></th>
                  <SortHeader field="name" label="Name" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                  <SortHeader field="business_name" label="Business" sortField={sortField} sortDir={sortDir} onSort={onSort} className="hide-xs" />
                  <th className="hide-xs">Source</th>
                  <SortHeader field="grade" label="Grade" sortField={sortField} sortDir={sortDir} onSort={onSort} className="hide-xs" />
                  <SortHeader field="status" label="Status" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                  <SortHeader field="created_at" label="Created" sortField={sortField} sortDir={sortDir} onSort={onSort} className="hide-xs" />
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {isLoadingLeads ? (
                  <tr><td colSpan={8} style={{ padding: 0 }}><TableSkeleton rows={6} cols={7} /></td></tr>
                ) : paginatedLeads.length === 0 ? (
                  <tr><td colSpan={8}>
                    <EmptyState
                      icon={Filter}
                      title="No leads match those filters"
                      body={hasActiveFilters ? 'Try clearing filters to see all leads.' : 'New leads will appear here as they come in.'}
                      action={hasActiveFilters ? <Btn data-testid="leads-tab-btn-5" variant="ghost" sm onClick={clearFilters}>Clear filters</Btn> : null}
                      testid="leads-empty"
                    />
                  </td></tr>
                ) : (
                  (sortedPage || []).map((lead) => {
                    const id = lead._id || lead.id;
                    const fullName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.name || 'Unnamed';
                    const isSel = selectedLeads.includes(id);
                    return (
                      <tr key={id} className={isSel ? 'selected' : ''} data-testid={`lead-row-${id}`}>
                        <td><Check data-testid="leads-tab-check-1" on={isSel} onClick={() => toggleOne(id)} testid={`select-${id}`} /></td>
                        <td>
                          <div className="lb-cell-name">
                            <Avatar name={fullName} size={28} />
                            <div>
                              <b>{fullName}</b>
                              <div><small>{lead.email}</small></div>
                            </div>
                          </div>
                        </td>
                        <td className="hide-xs">{lead.business_name || <span className="lb-cell-dim">—</span>}</td>
                        <td className="hide-xs"><SourceChip source={lead.source || 'contact'} /></td>
                        <td><Score grade={lead.grade} testid={`grade-${id}`} /></td>
                        <td><StatusBadge status={lead.status || 'new'} testid={`status-${id}`} /></td>
                        <td className="hide-xs lb-cell-dim">{lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', position: 'relative' }}>
                            <Btn data-testid="leads-tab-btn-4" variant="ghost" sm icon={EyeIcon} iconOnly onClick={() => onView(id)} testid={`view-lead-${id}`} title="View detail" />
                            <span ref={(el) => { shareAnchors.current[id] = { current: el }; }} style={{ position: 'relative' }}>
                              <Btn data-testid="leads-tab-btn-3" variant="ghost" sm icon={Share2} iconOnly onClick={() => setShareForLead({ id, name: fullName })} testid={`share-lead-${id}`} title="Share" />
                            </span>
                            <RowActions
                              testid={`row-actions-${id}`}
                              items={[
                                { icon: EyeIcon, title: 'Open detail', onClick: () => onView(id), testid: `row-view-${id}` },
                                { icon: Share2, title: 'Share', onClick: () => setShareForLead({ id, name: fullName }), testid: `row-share-${id}` },
                                ...(canDelete ? [{ icon: Trash2, title: 'Delete', danger: true, onClick: () => { setSelectedLeads([id]); setConfirmBulkDelete(true); }, testid: `row-delete-${id}` }] : []),
                              ]}
                            />
                            {shareForLead?.id === id && (
                              <Popover open onClose={() => setShareForLead(null)} anchorRef={shareAnchors.current[id]}>
                                <SharePanel
                                  leadName={shareForLead.name}
                                  onClose={() => setShareForLead(null)}
                                  onShare={async (payload) => {
                                    await onShare({ leadId: id, ...payload });
                                    setShareForLead(null);
                                  }}
                                  isLoading={isSharing}
                                  teamMembers={teamMembers}
                                />
                              </Popover>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <Pager
          page={currentPage}
          setPage={setCurrentPage}
          pageSize={pageSize || 25}
          setPageSize={setPageSize || (() => {})}
          total={total}
          testid="leads-pager"
        />
      </div>

      {/* Hard-stop Modal for bulk delete (only acceptable modal use per mockup) */}
      <Modal
        open={confirmBulkDelete}
        title={`Delete ${selectedLeads.length} lead${selectedLeads.length === 1 ? '' : 's'}?`}
        onClose={() => { setConfirmBulkDelete(false); setBulkDeleteReason(''); }}
        testid="bulk-delete-confirm"
        foot={(
          <>
            <Btn data-testid="leads-tab-btn-2" variant="ghost" sm onClick={() => { setConfirmBulkDelete(false); setBulkDeleteReason(''); }} testid="bulk-delete-cancel">Cancel</Btn>
            <Btn data-testid="leads-tab-btn-1"
              variant="danger"
              sm
              icon={Trash2}
              disabled={!bulkDeleteReason.trim() || isDeletingBulk}
              onClick={async () => {
                await onBulkDelete({ reason: bulkDeleteReason });
                setConfirmBulkDelete(false);
                setBulkDeleteReason('');
              }}
              testid="bulk-delete-confirm-button"
            >
              {isDeletingBulk ? 'Deleting…' : 'Delete leads'}
            </Btn>
          </>
        )}
      >
        <p style={{ margin: '0 0 12px', color: 'var(--text-2)', fontSize: 13.5 }}>
          This action cannot be undone. The audit log records this deletion with the reason you provide.
        </p>
        <label className="lb-field-row">
          <span className="lb-lbl">Reason *</span>
          <textarea
            className="lb-inp"
            rows={3}
            placeholder="Why are you deleting these leads?"
            value={bulkDeleteReason}
            onChange={(ev) => setBulkDeleteReason(ev.target.value)}
            data-testid="bulk-delete-reason"
            autoFocus
          />
        </label>
      </Modal>
    </div>
  );
}
